import axios from "axios";
import { USER_DETAILS } from "../utils/constants";

const api = axios.create({
  baseURL: process.env.REACT_APP_PUBLIC_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

const readSession = () => {
  try {
    const storedUserData = localStorage.getItem(USER_DETAILS);
    return storedUserData ? JSON.parse(storedUserData) : null;
  } catch (error) {
    return null;
  }
};

api.interceptors.request.use(
  async (config) => {
    const userData = readSession();

    if (userData && userData?.token) {
      config.headers["Authorization"] = `Bearer ${userData.token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

let pendingRefresh = null;

const refreshSession = (session) => {
  if (!pendingRefresh) {
    pendingRefresh = axios
      .post(`${process.env.REACT_APP_PUBLIC_API_URL}/auth/refresh`, {
        refreshToken: session.refreshToken,
      })
      .then(({ data }) => {
        localStorage.setItem(
          USER_DETAILS,
          JSON.stringify({
            ...readSession(),
            token: data.token,
            refreshToken: data.refreshToken,
          }),
        );
        return data.token;
      })
      .catch(() => null)
      .finally(() => {
        pendingRefresh = null;
      });
  }

  return pendingRefresh;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const request = error?.config;
    const session = readSession();

    if (
      error?.response?.status !== 401 ||
      !session?.token ||
      !session?.refreshToken ||
      request?._retried
    ) {
      return Promise.reject(error);
    }

    request._retried = true;
    const token = await refreshSession(session);

    if (!token) {
      localStorage.removeItem(USER_DETAILS);
      window.location.href = "/";
      return Promise.reject(error);
    }

    request.headers["Authorization"] = `Bearer ${token}`;
    return api(request);
  },
);

export const get = async (url, params = {}, headers = {}) => {
  try {
    const response = await api.get(url, { params }, headers);
    return response?.data;
  } catch (error) {
    throw error;
  }
};

export const post = async (url, data, headers = {}) => {
  try {
    const response = await api.post(url, data, headers);
    return response?.data;
  } catch (error) {
    throw error;
  }
};

export const put = async (url, data, headers = {}) => {
  try {
    const response = await api.put(url, data, headers);
    return response?.data;
  } catch (error) {
    throw error;
  }
};

export const patch = async (url, data, headers = {}) => {
  try {
    const response = await api.patch(url, data, headers);
    return response?.data;
  } catch (error) {
    throw error;
  }
};

export const del = async (url, headers = {}) => {
  try {
    const response = await api.delete(url, headers);
    return response?.data;
  } catch (error) {
    throw error;
  }
};

export default api;
